import { useCallback } from 'react';

import { useIsCoinsCurrency } from '@/feature/betslip-sbk/hooks/use-coins-currency';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { validateStakeInput } from '@/feature/betslip-sbk/utils/validators';
import { validateCoinsInput } from '@/feature/betslip-sbk/utils/validators-coins';
import { validatePredictionsStakeInput } from '@/feature/betslip-sbk/utils/validators-predictions';
import { activeLimitsSelector, useUserLimits } from '@/feature/responsible-gaming/hooks/use-user-limits';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useWallet } from '@/hooks/use-wallet';

const getStakeInputValidator = (isPredictionProduct: boolean, isCoinsCurrency: boolean) => {
    if (isPredictionProduct) {
        return validatePredictionsStakeInput;
    }
    if (isCoinsCurrency) {
        return validateCoinsInput;
    }
    return validateStakeInput;
};

/**
 * Returns a function that validates stake input and updates betslip state with the stake input errors
 */
export const useStakeInputValidation = () => {
    const { data: limits } = useUserLimits({ select: activeLimitsSelector('SINGLE_WAGER') });
    const updateInputStakeError = useSbkBetSlipStore(state => state.actions.updateStakeInputErrors);
    const { realMoneyTotal: walletCashBalance, betrBucks: walletBetrBucksBalance } = useWallet();
    const isCoinsCurrency = useIsCoinsCurrency();
    const isPredictionProduct = useIsPredictionsProduct();
    const validator = getStakeInputValidator(isPredictionProduct, isCoinsCurrency);

    return useCallback(
        (betId: string, betStake: number, isBetrBucks: boolean, stakeLimit?: number) => {
            if (betStake === 0) {
                updateInputStakeError(betId, undefined);
            } else {
                const error = validator({
                    limits: limits ?? [],
                    betStake,
                    walletCashBalance,
                    walletBetrBucksBalance,
                    isBetrBucks,
                    stakeLimit,
                });
                updateInputStakeError(betId, error);
            }
        },
        [limits, updateInputStakeError, walletBetrBucksBalance, walletCashBalance, validator]
    );
};
